import { useState, useEffect } from 'react'
import { createStructuredSelector } from 'reselect'
import Formsy from 'formsy-react'
import omit from 'lodash.omit'

import Modal from '@components/Modal'

import { withTranslate } from '@i18n/utils'

import { AddCreditCard } from 'credit-cards'
import useRequestPending from 'shared/hooks/useRequestPending'

import { PAYMENT_METHOD_TYPE_CREDIT_CARD } from 'auth/constants'
import {
    updateSubscriptionPendingSelector,
    paymentMethodsSelector,
    savePadPendingSelector,
} from 'dashboard-subscriptions/selectors'
import { updateSubscription } from 'dashboard-subscriptions/actions'
import {
    ADDRESS_TYPE_SHIPPING,
    CONTACT_TYPE_SHIPPING,
    CONTACT_TYPE_BILLING,
    SUBSCRIPTION_STATUS_ACTIVE,
} from 'dashboard-subscriptions/constants'

import SubscriptionModalFooter from 'dashboard-subscriptions/components/SubscriptionModalFooter'
import BillingTabForm from 'dashboard-subscriptions/components/BillingTab/BillingTabForm'
import PadModal from 'dashboard-subscriptions/components/BillingTab/PadModal'

import { objectsEqual } from 'shared/utils'
import { clearPending } from 'api/actions'
import { PAYMENT_METHOD_TYPE_PAD } from '../../../auth/constants'

const BillingTab = ({
    subscription,
    cards,
    translate: t,
    updateSubscription,
    updateSubscriptionPending,
    closeModal,
    billingRef,
    clearPending,
    savePadPending,
}) => {
    const billing = subscription.contacts?.find(c => c.contactType === CONTACT_TYPE_BILLING)
    const shipping = subscription.contacts?.find(c => c.contactType === CONTACT_TYPE_SHIPPING)
    const paymentMethod = subscription.paymentMethods ? subscription.paymentMethods[0] : {}
    const fieldsDisabled = subscription.state !== SUBSCRIPTION_STATUS_ACTIVE

    const [shippingSameAsBilling, setShippingSameAsBilling] = useState(
        objectsEqual([billing?.address, shipping?.address], ['type'])
    )
    const [showAddCardModal, setShowAddCardModal] = useState(false)
    const [showPADModal, setShowPADModal] = useState(false)
    const [onChangeData, setOnChangeData] = useState({})

    useEffect(() => {
        clearPending(['updateSubscription'])
    }, [])

    useRequestPending(savePadPending, () => {
        const padCards = cards.filter(({ paymentMethodType }) => paymentMethodType === PAYMENT_METHOD_TYPE_PAD)
        const newCard = padCards.length > 0 ? padCards[padCards.length - 1] : null

        if (newCard) {
            billingRef.current.updateInputsWithValue({
                billingCreditCard: newCard.value,
            })
        }
    })

    const { error, isLoading } = useRequestPending(updateSubscriptionPending)

    const submit = data => {
        const selectedCard = cards.find(c => data.billingCreditCard === c.value)
        const paymentMethods = []

        if (selectedCard) {
            paymentMethods.push(
                selectedCard.paymentMethodType === PAYMENT_METHOD_TYPE_CREDIT_CARD
                    ? selectedCard.nAmFields
                    : omit(selectedCard, ['value'])
            )
        }

        const newBillingAddress = {
            ...billing,
            ...data.billing,
        }

        const newShippingAddress = shippingSameAsBilling
            ? {
                  ...newBillingAddress,
                  contactType: CONTACT_TYPE_SHIPPING,
                  address: { ...newBillingAddress.address, type: ADDRESS_TYPE_SHIPPING },
              }
            : {
                  ...shipping,
                  ...data.shipping,
                  contactType: CONTACT_TYPE_SHIPPING,
              }

        const submitData = {
            ...subscription,
            contacts: [newBillingAddress, newShippingAddress],
            paymentMethods,
            autoPay: selectedCard.paymentMethodType === PAYMENT_METHOD_TYPE_CREDIT_CARD,
        }

        if (submitData.extended.fieldMap.paymentType.value === 'EXTERNAL') {
            submitData.extended.fieldMap.paymentType.value = 'SAVEDPAYMENT'
        }

        updateSubscription(submitData)
    }

    return (
        <div className="pb-4">
            {showPADModal && <PadModal onClose={() => setShowPADModal(false)} />}
            {showAddCardModal && (
                <Modal onClose={() => setShowAddCardModal(false)}>
                    <AddCreditCard successCallback={() => setShowAddCardModal(false)} />
                </Modal>
            )}
            <Formsy
                onSubmit={data => submit(data)}
                ref={billingRef}
                onChange={(currentValues, isChanged) => {
                    isChanged &&
                        currentValues.billingCreditCard !== onChangeData.billingCreditCard &&
                        setOnChangeData(currentValues)
                }}>
                <BillingTabForm
                    billing={billing}
                    shipping={shipping}
                    shippingSameAsBilling={shippingSameAsBilling}
                    setShippingSameAsBilling={setShippingSameAsBilling}
                    cards={cards}
                    paymentMethod={paymentMethod}
                    showAddCardModal={() => setShowAddCardModal(true)}
                    fieldsDisabled={fieldsDisabled}
                    showPadModal={() => setShowPADModal(true)}
                    subscription={subscription}
                    onChangeBillingData={() => submit(onChangeData)}
                />
                <SubscriptionModalFooter
                    isLoading={isLoading}
                    error={error}
                    closeModal={closeModal}
                    mainActionLabel={t('dashboard.account.save')}
                    disableMainAction={fieldsDisabled}
                    disableCancelSubscription={fieldsDisabled}
                />
            </Formsy>
        </div>
    )
}

export default compose(
    withTranslate,
    connect(
        createStructuredSelector({
            updateSubscriptionPending: updateSubscriptionPendingSelector,
            savePadPending: savePadPendingSelector,
            cards: paymentMethodsSelector,
        }),
        {
            clearPending,
            updateSubscription,
        }
    )
)(BillingTab)
